import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {throwError} from 'rxjs';

import {ModalService, NotificationService} from '@shared';

import {ActiveEmployeeService, EmployeeService, NavigationService, PatientService, TaskService} from 'planner-shared';

import {EmployeeListComponent} from './list/employee-list.component';
import {EmployeeInfoComponent} from "./info/employee-info.component";
import {RosterService} from "../shared/services/roster/roster.service";
import {FeatureFlags, FeatureFlagService, OngoingRequestTrackerService} from "@core";
import {RosterUpdateModalComponent} from "./roster-update-modal/roster-update-modal.component";

@UntilDestroy()
@Component({
    selector: 'app-employee',
    templateUrl: './employee.component.html',
    styleUrls: ['./employee.component.scss'],
    providers: [ActiveEmployeeService],
    standalone: false
})
export class EmployeeComponent implements OnInit {
    private readonly navigationService = inject(NavigationService);
    private readonly employeeService = inject(EmployeeService);
    private readonly activeEmployeeService = inject(ActiveEmployeeService);
    private readonly notificationService = inject(NotificationService);
    private readonly patientService = inject(PatientService);
    private readonly taskService = inject(TaskService);
    private readonly rosterService = inject(RosterService);
    private readonly progressIndicatorService = inject(OngoingRequestTrackerService);
    private readonly modalService = inject(ModalService);
    private readonly featureFlagService = inject(FeatureFlagService);

    public updating: boolean;
    public editing: boolean;
    @ViewChild(EmployeeListComponent) private readonly employeeListComponent: EmployeeListComponent;
    @ViewChild(EmployeeInfoComponent) private employeeInfoComponent: EmployeeInfoComponent;

    ngOnInit(): void {
        this.navigationService
            .saveButtonClick()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.updateEmployees());

        this.navigationService
            .resetButtonClick()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.restoreEmployees());

        this.progressIndicatorService.isInProgress().subscribe(() => {
            this.preventNavigationIfNoRostersScenario();
        });
    }

    public employeeInfoChanged(): void {
        this.employeeService.getEmployees().subscribe(employees => {
            const noError = employees.every(emp => !emp.hasError);
            this.editing = true;
            this.navigationService.disableNextButton(!noError);
            this.navigationService.showSaveAndResetButtons(noError);
            this.navigationService.activateSaveAndResetButtons(noError);
        });
        this.employeeListComponent.checkIfAllSelected();
        this.employeeListComponent.checkIfAllSelectedRideSharing();
    }

    public droppedTask(enableDragging: boolean): void {
        this.editing = enableDragging;
    }

    private updateEmployees(): void {
        this.featureFlagService.isEnabled(FeatureFlags.CriticalRosterPriority).subscribe(isCriticalRosterEnabled => {
            this.employeeService.getEmployees().subscribe(employees => {
                if (employees.some(employee => employee.editing && employee.rosterDepartmentId && !employee.rosterDepartmentName) && isCriticalRosterEnabled) {
                    let severalRosterTasks = false;
                    for (let employee of employees.filter(employee => employee.editing && employee.rosterDepartmentId)) {
                        this.rosterService.areDifferentRosterTasksOnEmployee(employee)
                            .subscribe(value =>
                                severalRosterTasks = value
                            );
                        if (severalRosterTasks) { break; }
                    }
                    this.modalService.openCustomModal(RosterUpdateModalComponent, () => this.performEmployeeUpdate(), null, severalRosterTasks, false);
                } else {
                    this.performEmployeeUpdate();
                }
            });
        });
    }

    private performEmployeeUpdate() {
        this.updating = true;
        this.navigationService.activateSaveAndResetButtons(false);

        this.employeeService
            .updateEditedEmployeesInfo()
            .pipe(
                tap(() => {
                    this.activeEmployeeService.updateActiveEmployeeInfo();
                    this.notificationService.success('EMPLOYEES_UPDATED');
                }),
                catchError(() => {
                    this.navigationService.activateSaveAndResetButtons(true);
                    return throwError('Update employee failed.');
                }),
                tap(() => {
                    this.editing = false;
                    this.updating = false;
                    this.taskService.reloadGeneralTasks();
                    this.employeeListComponent.loadActiveEmployeesCount();
                    this.navigationService.showSaveAndResetButtons(false);
                    this.preventNavigationIfNoRostersScenario();
                }),
                switchMap(() => this.patientService.reloadPatientsFeasibility())
            )
            .subscribe(() => {
                this.employeeInfoComponent.shiftSplitEmitter.emit();
            });
    }

    private restoreEmployees(): void {
        this.employeeService.getSelectedEmployee().subscribe(employee => {
            this.editing = false;
            this.employeeService.initEmployees();
            this.employeeListComponent.loadEmployees(employee.workHourId);
            this.navigationService.showSaveAndResetButtons(false);
        });
    }

    private preventNavigationIfNoRostersScenario() {
        this.featureFlagService.isEnabled(FeatureFlags.CriticalRosterPriority).subscribe(isCriticalRosterEnabled => {
            if (isCriticalRosterEnabled) {
                this.employeeService.getEmployees().subscribe(employees => {
                    this.rosterService.areEmployeesRosterMissingCase(employees).subscribe(rosterMissingCase => {
                        this.navigationService.disableNextButton(rosterMissingCase.valueOf());
                    });
                });
            }
        });
    }
}
